import { useState } from "react";
import { Star, ExternalLink, ChevronLeft, ChevronRight } from "lucide-react";
import { GOOGLE_MAPS_REVIEWS_URL, GOOGLE_RATING, REVIEWS } from "../lib/data";

// Multicolor Google "G" SVG
const GoogleG = ({ className = "w-7 h-7" }) => (
  <svg viewBox="0 0 48 48" className={className} aria-hidden="true">
    <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" />
    <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" />
    <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0124 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
    <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 01-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" />
  </svg>
);

// Star row with optional partial fill (for 4.4)
function StarRow({ value, size = "w-5 h-5" }) {
  const full = Math.floor(value);
  const partial = value - full;
  const stars = [];
  for (let i = 0; i < 5; i++) {
    if (i < full) {
      stars.push(<Star key={i} className={`${size} fill-[#fbbc04] text-[#fbbc04]`} />);
    } else if (i === full && partial > 0) {
      stars.push(
        <span key={i} className="relative inline-block">
          <Star className={`${size} text-slate-300`} />
          <span className="absolute inset-0 overflow-hidden" style={{ width: `${partial * 100}%` }}>
            <Star className={`${size} fill-[#fbbc04] text-[#fbbc04]`} />
          </span>
        </span>
      );
    } else {
      stars.push(<Star key={i} className={`${size} text-slate-300`} />);
    }
  }
  return <div className="flex items-center gap-0.5">{stars}</div>;
}

function ReviewCard({ review, testid }) {
  const [expanded, setExpanded] = useState(false);
  const isLong = review.text.length > 220;
  const displayText = expanded || !isLong ? review.text : review.text.slice(0, 220).trim() + "…";

  return (
    <article
      data-testid={testid}
      className="bg-white border border-slate-200 rounded-2xl p-6 flex flex-col gap-4 h-full hover:border-[#013368] hover:shadow-md transition-all"
    >
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-full bg-[#013368] text-white flex items-center justify-center font-display font-bold text-lg shrink-0">
          {review.initial}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-slate-900 truncate">{review.name}</div>
          <div className="text-xs text-slate-500 mt-0.5 truncate">{review.role}</div>
        </div>
        <GoogleG className="w-5 h-5 shrink-0 mt-1" />
      </div>

      <div className="flex items-center gap-2">
        <StarRow value={review.rating} size="w-4 h-4" />
      </div>

      <p className="text-slate-700 text-sm leading-relaxed">
        {displayText}{" "}
        {isLong && (
          <button
            onClick={() => setExpanded(!expanded)}
            data-testid={`${testid}-toggle`}
            className="text-[#013368] font-semibold hover:underline text-sm"
          >
            {expanded ? "mostra meno" : "leggi tutto"}
          </button>
        )}
      </p>
    </article>
  );
}

export default function GoogleReviewsBadge() {
  const [page, setPage] = useState(0);
  const perPage = 2; // mobile shows 1 via grid; we use index for arrows
  const total = REVIEWS.length;

  const prev = () => setPage((p) => (p - 1 + total) % total);
  const next = () => setPage((p) => (p + 1) % total);

  return (
    <section className="py-20 md:py-28 bg-white" data-testid="google-reviews-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="overline">Recensioni verificate</div>
          <div className="divider-red mx-auto mt-3 mb-6" />
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
            Cosa dicono i nostri clienti su Google
          </h2>
          <p className="mt-4 text-slate-600">
            Recensioni reali pubblicate dai clienti sulla nostra scheda Google. Trasparenza al 100%.
          </p>
        </div>

        {/* HERO BADGE: Rating + CTA */}
        <div className="max-w-5xl mx-auto mb-10" data-testid="google-reviews-badge">
          <div className="relative bg-gradient-to-br from-slate-50 to-white border border-slate-200 rounded-3xl p-8 md:p-10 overflow-hidden">
            <div className="absolute -top-6 -right-6 w-32 h-32 bg-[#e6eff8] rounded-full blur-3xl opacity-70 pointer-events-none" />
            <div className="absolute -bottom-8 -left-8 w-40 h-40 bg-red-50 rounded-full blur-3xl opacity-50 pointer-events-none" />

            <div className="relative grid md:grid-cols-12 gap-6 items-center">
              <div className="md:col-span-5 flex items-center gap-5">
                <GoogleG className="w-14 h-14 shrink-0" />
                <div>
                  <div className="text-xs uppercase tracking-widest font-semibold text-slate-500">
                    {GOOGLE_RATING.totalLabel}
                  </div>
                  <div className="flex items-baseline gap-2 mt-1">
                    <span className="font-display font-extrabold text-5xl md:text-6xl text-slate-900 leading-none">
                      {GOOGLE_RATING.scoreDisplay}
                    </span>
                    <span className="text-slate-400 text-lg">/ 5</span>
                  </div>
                  <div className="mt-2">
                    <StarRow value={GOOGLE_RATING.score} />
                  </div>
                </div>
              </div>

              <div className="md:col-span-7 md:border-l md:border-slate-200 md:pl-8">
                <p className="text-slate-700 text-base leading-relaxed">
                  Da oltre 50 anni costruiamo il rapporto con i nostri clienti sulla base di{" "}
                  <strong>fiducia, competenza e trasparenza</strong>. Leggi tutte le recensioni
                  direttamente sulla nostra scheda Google.
                </p>
                <div className="mt-5 flex flex-wrap gap-3">
                  <a
                    href={GOOGLE_MAPS_REVIEWS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="google-reviews-read-button"
                    className="btn-secondary inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm"
                  >
                    <GoogleG className="w-4 h-4" /> Leggi tutte le recensioni
                    <ExternalLink className="w-3.5 h-3.5 opacity-70" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* REVIEW CARDS GRID */}
        <div className="hidden md:grid grid-cols-1 md:grid-cols-2 gap-5">
          {REVIEWS.map((r, i) => (
            <ReviewCard key={i} review={r} testid={`review-card-${i}`} />
          ))}
        </div>

        {/* MOBILE CAROUSEL */}
        <div className="md:hidden">
          <div className="relative">
            <ReviewCard review={REVIEWS[page]} testid={`review-card-mobile-${page}`} />
          </div>
          <div className="flex items-center justify-between mt-5">
            <button
              onClick={prev}
              data-testid="reviews-prev"
              className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center text-[#013368] hover:border-[#013368] transition"
              aria-label="Recensione precedente"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="flex gap-1.5">
              {REVIEWS.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setPage(i)}
                  data-testid={`reviews-dot-${i}`}
                  className={`w-2 h-2 rounded-full transition-all ${
                    i === page ? "bg-[#013368] w-6" : "bg-slate-300"
                  }`}
                  aria-label={`Vai alla recensione ${i + 1}`}
                />
              ))}
            </div>
            <button
              onClick={next}
              data-testid="reviews-next"
              className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center text-[#013368] hover:border-[#013368] transition"
              aria-label="Recensione successiva"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        <p className="text-center text-xs text-slate-500 mt-8 max-w-2xl mx-auto">
          Recensioni pubblicate dai clienti sulla scheda Google ufficiale di Il Velocipede.
          Contenuti © dei rispettivi autori · Google e il logo Google sono marchi di Google LLC.
        </p>
      </div>
    </section>
  );
}
